import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { PageResult } from '@spike/model';
import { Room } from '@spike/room-model';
import { fetchThunk as fetchRoomsThunk } from '@spike/rooms-action';
import { fetchStaffThunk } from 'actions/staff/StaffActions';
import { saveOpsTaskThunk } from 'actions/opsTasks/OpsTasksActions';
import Button from 'components/UI/V3/Button/Button';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { useDrawerContext } from 'components/UI/V3/Drawer/DrawerContext';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { Select, SelectOption } from 'components/UI/V3/Forms/Select/Select';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';
import { TimeSelector } from 'components/UI/V3/Forms/TimeSelector/TimeSelector';
import TagsSelector from 'components/UI/V3/TagsSelector/TagsSelector';
import { TagOption } from 'components/UI/V3/TagsSelector/TagOption';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import moment, { Moment } from 'moment-timezone';
import Staff from 'model/Staff';
import { OpsTask, OpsTaskInput } from 'model/OpsTask';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OpsTasksStatus } from 'reducers/opsTasks/OpsTasksState';
import { StaffStatus } from 'reducers/staff/StaffState';
import { RootState } from 'store';
import styles from './OpsTaskForm.module.scss';

interface OpsTaskFormProps {
    taskToEdit?: OpsTask;
    zIndex?: number;
    onClose: () => void;
}

interface TimeOption {
    id: string;
    label: string;
}

const TIME_FORMAT = 'h:mma';
const TIME_STEP_MINUTES = 15;
const DESCRIPTION_MAX_LENGTH = 500;

const buildTimeOptions = (): Array<TimeOption> => {
    const options: Array<TimeOption> = [];
    const base = moment().startOf('day');
    for (let minutes = 0; minutes < 24 * 60; minutes += TIME_STEP_MINUTES) {
        const formatted = base.clone().add(minutes, 'minutes').format(TIME_FORMAT);
        options.push({ id: formatted, label: formatted });
    }
    return options;
};

const snapToStep = (value: Moment): Moment =>
    value
        .clone()
        .minute(Math.ceil(value.minute() / TIME_STEP_MINUTES) * TIME_STEP_MINUTES)
        .second(0)
        .millisecond(0);

const OpsTaskFormContent: FunctionComponent<{ taskToEdit?: OpsTask }> = ({ taskToEdit }) => {
    const dispatch = useDispatch();
    const timeZone = useTimeZone();
    const apiClientWrapper = useApiClientWrapper();
    const { close } = useDrawerContext();
    const { organizationMarketplaces } = useOrganizationMarketplaces();

    const staffMembers = useSelector((state: RootState) => state.staff.staff);
    const staffStatus = useSelector((state: RootState) => state.staff.status);
    const rooms = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);
    const saving = useSelector((state: RootState) => state.opsTasks.saving);
    const status = useSelector((state: RootState) => state.opsTasks.status);

    const timeOptions = useMemo(buildTimeOptions, []);

    const initialDate = useMemo(
        () => (taskToEdit ? snapToStep(taskToEdit.dueAt.clone()) : snapToStep(moment().tz(timeZone))),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const [selectedDate, setSelectedDate] = useState<Moment>(initialDate);
    const [isDateExpanded, setIsDateExpanded] = useState(false);
    const [description, setDescription] = useState(taskToEdit?.description ?? '');
    const [selectedStaffIds, setSelectedStaffIds] = useState<Array<number>>(taskToEdit?.staffIds ?? []);
    const [selectedRoomId, setSelectedRoomId] = useState<number | undefined>(taskToEdit?.roomIds?.[0]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const marketplaceIds = useMemo(
        () => organizationMarketplaces.map(marketplace => marketplace.id),
        [organizationMarketplaces]
    );

    useEffect(() => {
        if (staffMembers.length === 0 && staffStatus !== StaffStatus.Fetching) {
            dispatch(fetchStaffThunk());
        }
        if (rooms.page.length === 0) {
            dispatch(fetchRoomsThunk(apiClientWrapper, 1, 3210, marketplaceIds));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const wasSavingRef = useRef(false);
    useEffect(() => {
        if (saving) {
            wasSavingRef.current = true;
            return;
        }
        if (wasSavingRef.current && isSubmitting) {
            wasSavingRef.current = false;
            if (status === OpsTasksStatus.Error) {
                setIsSubmitting(false);
            } else {
                close();
            }
        }
    }, [saving, status, isSubmitting, close]);

    const activeStaff = useMemo<Array<Staff>>(
        () => staffMembers.filter(member => member.active && !member.deleted),
        [staffMembers]
    );

    const staffTagOptions = useMemo<Array<TagOption<number>>>(
        () =>
            activeStaff.map(member => ({
                id: member.id ?? 0,
                name: `${member.person.firstName} ${member.person.lastName}`,
                image: member.person.avatar
            })),
        [activeStaff]
    );

    const selectedStaffOptions = useMemo(
        () => staffTagOptions.filter(staff => selectedStaffIds.includes(staff.id)),
        [staffTagOptions, selectedStaffIds]
    );

    const isStaffLoading = staffStatus === StaffStatus.Fetching && staffMembers.length === 0;

    const roomOptions = useMemo<Array<SelectOption<number>>>(
        () => rooms.page.map(room => ({ label: room.name, value: String(room.id ?? 0) })),
        [rooms.page]
    );

    const selectedTimeOption = useMemo(
        () => timeOptions.find(option => option.id === selectedDate.format(TIME_FORMAT)),
        [timeOptions, selectedDate]
    );

    const isValid = description.trim().length > 0;

    const handleDateChange = (value: Moment | null) => {
        if (!value) {
            return;
        }
        setSelectedDate(value.clone().hour(selectedDate.hour()).minute(selectedDate.minute()).second(0).millisecond(0));
        setIsDateExpanded(false);
    };

    const handleTimeChange = (value: string) => {
        const time = moment(value, TIME_FORMAT);
        setSelectedDate(selectedDate.clone().hour(time.hour()).minute(time.minute()).second(0).millisecond(0));
    };

    const handleSubmit = () => {
        if (!isValid) {
            return;
        }

        const input: OpsTaskInput = {
            ...(taskToEdit && { id: taskToEdit.id }),
            description: description.trim(),
            dueAt: selectedDate,
            staffIds: selectedStaffIds,
            roomIds: selectedRoomId !== undefined ? [selectedRoomId] : []
        };

        setIsSubmitting(true);
        dispatch(saveOpsTaskThunk(input));
    };

    return (
        <>
            <DrawerHeader title={taskToEdit ? 'Edit Task' : 'Add Task'} />

            <DrawerBody className={styles.body}>
                <div className={styles.dateTimeRow}>
                    <ExpandableSelector
                        title="Date"
                        id="ops-task-date"
                        expanded={isDateExpanded}
                        label={selectedDate.format('ddd, DD MMM YYYY')}
                        onExpand={() => setIsDateExpanded(!isDateExpanded)}
                        onClickAway={() => setIsDateExpanded(false)}
                    >
                        <DatePicker
                            minDate={moment().tz(timeZone).subtract(1, 'year')}
                            maxDate={moment().tz(timeZone).add(2, 'years')}
                            timeZone={timeZone}
                            value={selectedDate}
                            onChange={handleDateChange}
                        />
                    </ExpandableSelector>

                    <TimeSelector
                        label="Time"
                        value={selectedTimeOption}
                        options={timeOptions}
                        onChange={handleTimeChange}
                    />
                </div>

                <Textarea
                    label="Task"
                    value={description}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    placeholder="Type your task..."
                    onChange={event => setDescription(event.target.value)}
                />

                <TagsSelector
                    title="Add Staff Members"
                    direction="up"
                    addButtonColor="black"
                    selectAllLabel="All Members"
                    selectAllIcon={faUsers}
                    tags={staffTagOptions}
                    selected={selectedStaffOptions}
                    loading={isStaffLoading}
                    onSelect={id => setSelectedStaffIds(prev => (prev.includes(id) ? prev : [...prev, id]))}
                    onSelectAll={ids => setSelectedStaffIds(Array.from(new Set(ids)))}
                    onRemove={id => setSelectedStaffIds(prev => prev.filter(staffId => staffId !== id))}
                />

                <Select
                    label="Room"
                    placeholder="Select Room"
                    options={roomOptions}
                    value={selectedRoomId !== undefined ? String(selectedRoomId) : undefined}
                    onChange={value => setSelectedRoomId(value ? Number(value) : undefined)}
                />
            </DrawerBody>

            <DrawerFooter>
                <Button
                    label="Save"
                    size="large"
                    variant="green"
                    disabled={!isValid}
                    isLoading={saving}
                    onClick={handleSubmit}
                />
            </DrawerFooter>
        </>
    );
};

export const OpsTaskForm: FunctionComponent<OpsTaskFormProps> = ({ taskToEdit, zIndex, onClose }) => (
    <Drawer
        zIndex={zIndex}
        onClose={onClose}
    >
        <OpsTaskFormContent taskToEdit={taskToEdit} />
    </Drawer>
);

export default OpsTaskForm;
